import React, {useCallback, useEffect} from "react";
import {useEntityLoader, UseEntityLoaderProps} from "../../hooks/entites/useEntityLoader";
import EntityForm from "./EntityForm";
import {isErrorResponse} from "../../utils/utils";
import {useToastContext} from "../../context/toastContext";
import {useTranslation} from "react-i18next";
import {BaseEntity, BaseEntityNameLess} from "../../api/entities/BaseEntity";
import {setEntity} from "../../redux/entity/entity-reducer";
import {useDispatch} from "react-redux";
import UndoLastOperationButton
    from "../../project-modes/RoadNetworkMode/RoadNetworkManager/UndoLastOperationButton/UndoLastOperationButton";
import {setSourceReloadRequired} from "../../redux/map/map-reducer";
import {useLocation, useNavigate} from "react-router-dom";
import {LoadingBackdrop} from "../LoadingBackdrop/LoadingBackdrop";
import {usePresetsContext} from "../../context/presetsContext";
import {useLayersInteractivity} from "../../hooks/map/useLayersInteractivity";


//TODO loadByDefault is to avoid entity load in TransitRouteEdit => update architecture
type Props = {
    entityId: string;
    entityName: string;
    layerId: string;
    getUrlOnSaveFn?: (resp: any) => string | undefined | Promise<string | undefined>;
    getUrlOnDeleteFn?: (resp: any) => string | undefined;
    actionButtons?: JSX.Element | JSX.Element[];
    undoLastActionBtn?: boolean;
    loadByDefault?: boolean;
} & UseEntityLoaderProps

const EntityEditor = <T extends BaseEntityNameLess>({
                                                        entityId,
                                                        entityName,
                                                        layerId,
                                                        saveFnArgs,
                                                        presetProperty,
                                                        getUrlOnSaveFn,
                                                        getUrlOnDeleteFn,
                                                        actionButtons,
                                                        undoLastActionBtn = false,
                                                        entityView,
                                                        loadByDefault = true
                                                    }: Props) => {
    const {selectedPresetId} = usePresetsContext();
    const {
        loading,
        entity,
        metadata,
        loadData,
        saveFn,
        deleteFn
    } = useEntityLoader<T>({entityName, saveFnArgs, presetProperty});
    const {setDrawing} = useLayersInteractivity();
    const {addToast} = useToastContext();
    const {t} = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const updatedEntityId = entityId === 'new' ? null : entityId;

    useEffect(() => {
        if (loadByDefault && selectedPresetId) {
            (async function () {
                //TODO redirect to list or project if entity doesn't exist
                await loadData({entityName, entityId: updatedEntityId, entityView});
            })();
        }
    }, [entityName, updatedEntityId, entityView, loadByDefault, selectedPresetId]);

    const changeFormHandler = useCallback((formDataObj: { [key: string]: string | number }) => {
        dispatch(setEntity({entityName, entity: {...entity, ...formDataObj}}));
    }, [entity])

    const saveEntity = useCallback(async (formDataObj: { [key: string]: string | number }) => {
        const abortController = new AbortController();

        const resp = await saveFn({
            id: updatedEntityId,
            entityName,
            data: formDataObj,
            abortSignal: abortController.signal
        });

        if (isErrorResponse(resp)) {
            addToast(`${resp.error_description}`, 'error');
        } else {
            addToast(t('common.save-successful'), 'success');
            await loadData({entityName, entityId: updatedEntityId, entityView});
            if (layerId) {
                dispatch(setSourceReloadRequired({sourceId: layerId, isReloadRequired: true}));
            }
            setDrawing(false);

            if (getUrlOnSaveFn) {
                const url = await getUrlOnSaveFn?.(resp);
                if (url) {
                    navigate(url, {state: {prevUrl: location.pathname}, relative: "path"});
                }
            }
        }

    }, [entityName, updatedEntityId, layerId, getUrlOnSaveFn, setDrawing]);

    const deleteEntity = useCallback(async ({entity}: { entity: T }) => {
        const resp = await deleteFn?.({entity});

        if (isErrorResponse(resp)) {
            addToast(`${resp.error_description}`, 'error');
        } else {
            addToast(t('common.delete-successful'), 'success');

            if (layerId) {
                dispatch(setSourceReloadRequired({sourceId: layerId, isReloadRequired: true}));
            }

            if (getUrlOnDeleteFn) {
                const url = getUrlOnDeleteFn?.(resp);
                if (url) {
                    navigate(url, {state: location.pathname, relative: "path"});
                }
            }
        }
    }, [layerId])

    const onLastActionUndo = useCallback(async () => {
        await loadData({entityName, entityId: updatedEntityId});
    }, [loadData, updatedEntityId, entityName]);

    return (
        <LoadingBackdrop isVisible={loading || !Object.values(entity).length}>
            <EntityForm
                loading={loading}
                entity={entity}
                metadata={metadata}
                changeFormFn={changeFormHandler}
                saveFn={saveEntity}
                deleteFn={deleteEntity as any}
                icons={
                    <>
                        {actionButtons}
                        {
                            undoLastActionBtn
                                ? <UndoLastOperationButton onFinishFn={onLastActionUndo}/>
                                : null
                        }
                    </>
                }
            />
        </LoadingBackdrop>
    )
}

export default EntityEditor;